// External Imports
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import Alert from 'react-bootstrap/Alert';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';

import { PiChartLineLight } from 'react-icons/pi';

import AccessDenied from './AccessDenied';

//Hooks
import { useAuth } from 'react-oidc-context';
import useToken from '../utils/useToken';
import useBearerToken from '../utils/useBearerToken';
import ForbiddenError from '../utils/error';

import { fetchCustomer } from '../utils/customer';

export default function BandwidthPool() {
  const auth = useAuth();
  const { token, isValidToken } = useToken();
  const { isLoggedIn, getToken } = useBearerToken();
  const params = useParams();

  // Local Data
  const [customerId, setCustomerId] = useState(params.customerId);
  const [customer, setCustomer] = useState({});
  const [bandwidthPool, setBandwidthPool] = useState({});
  const [error, setError] = useState({ message: '', severity: 'primary', access_denied: false });
  const [loading, setLoading] = useState(true);

  const getCustomer = async () => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    setLoading(true);

    fetchCustomer(bearerToken, customerId)
      .then((data) => {
        setCustomer(data);
        setError({ message: '', severity: 'success', access_denied: false });
      })
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setError({ message: '', severity: 'danger', access_denied: true });
        } else {
          setError({
            message: 'Could not retrieve customer!',
            severity: 'danger',
            access_denied: false,
          });
        }
      });

    setLoading(false);
  };

  useEffect(() => {
    getCustomer();
  }, [customerId]);

  useEffect(() => {
    if (customer.bandwidth_pools != null) {
      const bw_pool = customer.bandwidth_pools.find((pool) => pool.pool_name === params.poolName);
      setBandwidthPool(bw_pool);
    }
  }, [customer]);

  return (
    <>
      {error.message != '' ? (
        <Alert variant={error.severity}>
          <span>{error.message}</span>
        </Alert>
      ) : (
        <>
          {error.access_denied ? (
            <AccessDenied />
          ) : (
            <Row>
              <Col>
                <h1 className='text-center'>Bandwidth Pool: {params.poolName}</h1>
              </Col>
            </Row>
          )}
        </>
      )}

      {loading ? (
        <Alert variant='warning'>
          <Spinner animation='border' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </Spinner>
          <span>Data loading...</span>
        </Alert>
      ) : (
        <>
          <Row className='mb-3'>
            <Col sm={4}>
              <Card>
                <Card.Header>Configuration</Card.Header>
                <Card.Body>
                  <Table striped>
                    <tbody>
                      <tr>
                        <th>Name</th>
                        <td>{bandwidthPool.pool_name}</td>
                      </tr>
                      <tr>
                        <th>Description</th>
                        <td>{bandwidthPool.pool_description}</td>
                      </tr>
                      <tr>
                        <th>Downlink MIR</th>
                        <td>{bandwidthPool.downlink_mir / 1024} Mbps</td>
                      </tr>
                      <tr>
                        <th>Uplink MIR</th>
                        <td>{bandwidthPool.uplink_mir / 1024} Mbps</td>
                      </tr>
                      <tr>
                        <th>Network</th>
                        <td>{bandwidthPool.uplink}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            {customer.subscriptions != null && customer.subscriptions.length > 0 && (
              <Col sm={8}>
                <Card>
                  <Card.Header>Attached Subscriptions</Card.Header>
                  <Card.Body>
                    <Table striped>
                      <thead>
                        <tr>
                          <th>&nbsp;</th>
                          <th>Site</th>
                          <th>Subnet</th>
                          <th>Subscription</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customer.subscriptions
                          .filter((sub) => sub.pool_name == params.poolName)
                          .map((sub) => (
                            <tr
                              key={sub.subnet}
                              className={
                                sub.status == 'active'
                                  ? 'table-success'
                                  : sub.status == 'suspended'
                                    ? 'table-warning'
                                    : 'table-danger'
                              }
                            >
                              <td>
                                <a
                                  className='btn btn-primary'
                                  href={'/customer/' + customerId + '/site/' + sub.site_id}
                                >
                                  {sub.site_id}
                                </a>
                              </td>
                              <td>
                                {customer.sites.find((site) => site.site_id === sub.site_id).name}
                              </td>
                              <td>{sub.subnet}</td>
                              <td>{sub.plan_name}</td>
                              <td>{sub.status}</td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <Row>
            <Col>
              <Card>
                <Card.Header>Bandwidth</Card.Header>
                <Card.Body>
                  <p className='text-center'>
                    <a
                      className='btn bi bi-building-exclamation'
                      href={
                        '/customer/' + customerId + '/bandwidth/bandwidth_pool/' + params.poolName
                      }
                    >
                      View Historical Bandwidth: <PiChartLineLight size='4em' />
                    </a>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
}
